/**
 * Utility functions for consistent date formatting throughout the application
 * All dates should be displayed in mm/dd/yyyy format
 *
 * TIMEZONE HANDLING:
 * - All dates are stored in UTC on the server
 * - Frontend converts local time to UTC when sending to server
 * - Frontend converts UTC back to local time when displaying
 * - When a timezone parameter is provided, dates are displayed in that timezone
 *   instead of the browser's local timezone
 * - Date-only fields (like DOB) are handled as local dates without timezone conversion
 */

/**
 * Detects EHR "empty date" sentinels. Some external systems (e.g. CareTracker,
 * any SQL Server source) emit 1899-12-30, 1899-12-31, or 1900-01-01 when a
 * date field is empty/null instead of omitting it. Only treat values at or
 * before 1900 as sentinels so real DOBs (e.g. patients born 1901+) render
 * correctly.
 */
export const isSentinelDate = (date: string | Date | null | undefined): boolean => {
  if (!date) return false;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return false;
  return d.getUTCFullYear() < 1901;
};

/**
 * Format a date string or Date object to mm/dd/yyyy format
 * @param date - Date string, Date object, or null/undefined
 * @param timezone - Optional IANA timezone (e.g. "America/New_York"). If provided, formats in that timezone.
 * @returns Formatted date string in mm/dd/yyyy format or fallback text
 */
export const formatDate = (date: string | Date | null | undefined, timezone?: string): string => {
  if (!date) return 'Not specified';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'Invalid date';

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(timezone && { timeZone: timezone }),
    };
    return dateObj.toLocaleDateString('en-US', options);
  } catch {
    return 'Invalid date';
  }
};

/**
 * Format a date with time in mm/dd/yyyy at h:mm AM/PM format
 * @param date - Date string, Date object, or null/undefined
 * @param timezone - Optional IANA timezone. If provided, formats in that timezone.
 * @returns Formatted date and time string
 */
export const formatDateTime = (date: string | Date | null | undefined, timezone?: string): string => {
  if (!date) return 'Not specified';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'Invalid date';

    const dateOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(timezone && { timeZone: timezone }),
    };
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...(timezone && { timeZone: timezone }),
    };

    const dateStr = dateObj.toLocaleDateString('en-US', dateOptions);
    const timeStr = dateObj.toLocaleTimeString('en-US', timeOptions);

    return `${dateStr} at ${timeStr}`;
  } catch {
    return 'Invalid date';
  }
};

/**
 * Format a time only in h:mm AM/PM format
 * @param date - Date string, Date object, or null/undefined
 * @param timezone - Optional IANA timezone. If provided, formats in that timezone.
 * @returns Formatted time string
 */
export const formatTime = (date: string | Date | null | undefined, timezone?: string): string => {
  if (!date) return 'Not specified';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'Invalid time';

    const options: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...(timezone && { timeZone: timezone }),
    };
    return dateObj.toLocaleTimeString('en-US', options);
  } catch {
    return 'Invalid time';
  }
};

/**
 * Read the calendar day of a date-only value such as a date of birth. These are stored as
 * YYYY-MM-DD or as UTC midnight of that day, so the day is read from the string or from the
 * UTC components. Reading local components would show the previous day west of UTC.
 * @returns { year, month (1-12), day } or null when missing, invalid, or an EHR empty-date sentinel
 */
export const dateOnlyParts = (
  date: string | Date | null | undefined
): { year: number; month: number; day: number } | null => {
  if (!date) return null;
  if (isSentinelDate(date)) return null;
  if (typeof date === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
    if (match) {
      const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
      // A real calendar day only: 2026-02-31 would roll over into March.
      const check = new Date(Date.UTC(year, month - 1, day));
      if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null;
      return { year, month, day };
    }
  }
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return null;
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
};

/**
 * Format a date-only value (date of birth) as mm/dd/yyyy for the day it names, in any timezone.
 * @returns Formatted date, or 'Not specified' when missing or invalid
 */
export const formatDateOnly = (date: string | Date | null | undefined): string => {
  const parts = dateOnlyParts(date);
  if (!parts) return 'Not specified';
  return `${String(parts.month).padStart(2, '0')}/${String(parts.day).padStart(2, '0')}/${parts.year}`;
};

/**
 * Whole years between a date of birth and today's local date.
 * @returns Age in years, or null when the date of birth is missing, invalid, or in the future
 */
export const ageInYears = (
  dateOfBirth: string | Date | null | undefined,
  today: Date = new Date()
): number | null => {
  const birth = dateOnlyParts(dateOfBirth);
  if (!birth) return null;
  let age = today.getFullYear() - birth.year;
  const month = today.getMonth() + 1;
  if (month < birth.month || (month === birth.month && today.getDate() < birth.day)) {
    age--;
  }
  return age < 0 ? null : age;
};

/**
 * Calculate age from date of birth
 * @param dateOfBirth - Date of birth string or Date object
 * @returns Age in years or 'Unknown' if invalid
 */
export const calculateAge = (dateOfBirth: string | Date | null | undefined): string => {
  if (!dateOfBirth) return 'Unknown';
  if (isSentinelDate(dateOfBirth)) return 'Unknown';
  if (!dateOnlyParts(dateOfBirth)) return 'Unknown';

  const age = ageInYears(dateOfBirth);
  if (age === null) return 'Invalid date';
  if (age === 0) return 'Less than 1 year';

  return `${age} year${age !== 1 ? 's' : ''}`;
};

/**
 * Date of birth with age for patient identity lines, e.g. "03/15/1980 (46 y)".
 * @returns null when no usable date of birth is recorded
 */
export const formatDobWithAge = (dateOfBirth: string | Date | null | undefined): string | null => {
  if (!dateOnlyParts(dateOfBirth)) return null;
  const age = ageInYears(dateOfBirth);
  return age === null ? formatDateOnly(dateOfBirth) : `${formatDateOnly(dateOfBirth)} (${age} y)`;
};

/**
 * Format an invitation expiry window for display
 * @param hours - Hours in the window or remaining until expiry
 * @returns Days (rounded up) when over 24 hours, otherwise hours
 */
export const formatExpiryWindow = (hours: number): string => {
  if (hours > 24) return `${Math.ceil(hours / 24)} days`;
  return `${hours} hour${hours !== 1 ? 's' : ''}`;
};

/**
 * Format date for HTML input elements (YYYY-MM-DD or YYYY-MM-DDTHH:mm)
 * @param date - Date string or Date object (assumed to be UTC from server)
 * @param includeTime - Whether to include time (for datetime-local inputs)
 * @returns Formatted string for HTML input elements in local timezone
 */
export const formatForInput = (date: string | Date | null | undefined, includeTime = false): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';
    
    if (includeTime) {
      // Convert UTC to local time for datetime-local input
      // datetime-local expects local time in YYYY-MM-DDTHH:mm format
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const hours = String(dateObj.getHours()).padStart(2, '0');
      const minutes = String(dateObj.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } else {
      // For date-only inputs, use local date without timezone conversion
      // This is for things like Date of Birth where timezone doesn't matter
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch {
    return '';
  }
};

/**
 * Convert local datetime to UTC for sending to server
 * @param localDateTime - Local datetime string from datetime-local input (YYYY-MM-DDTHH:mm)
 * @returns UTC datetime string in ISO format
 */
export const convertLocalToUTC = (localDateTime: string): string => {
  if (!localDateTime) return '';
  
  try {
    // Create date object from local datetime string
    // Note: new Date() with YYYY-MM-DDTHH:mm format creates a local date
    const localDate = new Date(localDateTime);
    if (isNaN(localDate.getTime())) return '';
    
    // Convert to UTC ISO string
    return localDate.toISOString();
  } catch {
    return '';
  }
};

/**
 * Convert UTC datetime from server to local datetime for display
 * @param utcDateTime - UTC datetime string from server
 * @returns Local datetime string
 */
export const convertUTCToLocal = (utcDateTime: string | Date | null | undefined): Date | null => {
  if (!utcDateTime) return null;
  
  try {
    const utcDate = typeof utcDateTime === 'string' ? new Date(utcDateTime) : utcDateTime;
    if (isNaN(utcDate.getTime())) return null;
    
    // Return the Date object which will automatically be in local time when displayed
    return utcDate;
  } catch {
    return null;
  }
};

/**
 * Convert date-only string to UTC date (for fields like Date of Birth)
 * @param dateOnly - Date string in YYYY-MM-DD format
 * @returns UTC date string preserving the date (no timezone conversion)
 */
export const convertDateOnlyToUTC = (dateOnly: string): string => {
  if (!dateOnly) return '';
  
  try {
    // For date-only fields, we want to preserve the exact date
    // regardless of timezone. We create a UTC date with the same day/month/year
    const parts = dateOnly.split('-');
    if (parts.length !== 3) return '';
    
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1; // Month is 0-based
    const day = parseInt(parts[2]);
    
    // Create UTC date with the specified date
    const utcDate = new Date(Date.UTC(year, month, day));
    return utcDate.toISOString();
  } catch {
    return '';
  }
};

/**
 * Convert UTC date to date-only string (for fields like Date of Birth)
 * @param utcDate - UTC date from server
 * @returns Date string in YYYY-MM-DD format
 */
export const convertUTCToDateOnly = (utcDate: string | Date | null | undefined): string => {
  if (!utcDate) return '';

  try {
    const dateObj = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
    if (isNaN(dateObj.getTime())) return '';
    // Extract UTC date components to preserve the exact date
    const year = dateObj.getUTCFullYear();
    const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getUTCDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
};

// ============================================================================
// EHR Pre-Localized Time Helpers
// ============================================================================
// Some EHR systems (e.g. CareTracker) send times already converted to the
// practice's local timezone. These are stored as-is in UTC so that no
// server timezone shifts the value. Use these helpers to display the stored
// UTC value directly, bypassing the browser's local timezone conversion.
// For EHRs that send real UTC, use the standard format() / toLocaleTimeString().

// EHR feature flags are defined in @/config/ehrFeatureFlags.ts
export { EHR_PRE_LOCALIZED_SYSTEMS } from '@/config/ehrFeatureFlags';

/**
 * Format time from a pre-localized EHR date (reads UTC components directly).
 */
export const formatEhrTime = (date: string | Date | null | undefined): string => {
  if (!date) return 'Not specified';
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return 'Invalid time';
    const h = d.getUTCHours();
    const m = d.getUTCMinutes();
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
  } catch { return 'Invalid time'; }
};

/**
 * Format date from a pre-localized EHR date (reads UTC components directly).
 */
export const formatEhrDate = (date: string | Date | null | undefined): string => {
  if (!date) return 'Not specified';
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return 'Invalid date';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
  } catch { return 'Invalid date'; }
};

/**
 * Format full date+time from a pre-localized EHR date.
 */
export const formatEhrDateTime = (date: string | Date | null | undefined): string => {
  if (!date) return 'Not specified';
  return `${formatEhrDate(date)} at ${formatEhrTime(date)}`;
};